import {
  keys,
  length,
  map,
  omit,
  toPairs,
  prop,
  compose,
  fromPairs,
  reduce,
  values,
  mergeDeepRight,
  replace,
  sum,
} from 'ramda';
import { filterFalsy } from 'yafrpl/arrays';

import { compact } from '../../helpers/compact';
import { getClient, getAlgoliaIndexName } from './algolia-api';
import {
  REGION,
  COMMON_THEMATIC,
  MENU,
  CLASSIFICATION,
  FINANCIAL_PUBLICATION,
  PUBLICATION,
  FILTER,
} from '../../types/filters';
import { MEDIA_TYPE } from '../../types/press-media';

const generateFacetFilters = (facets) => {
  const result = filterFalsy(
    map(
      ([filter, values]) =>
        length(values) && map((value) => `filters.${filter}:${value}`, values),
      toPairs(facets || {})
    )
  );
  return length(result) ? result : undefined;
};

const getLocalizedIndexedClient = (lang, sorted, agenda) => {
  const suffix = sorted ? '_sorted' : '';
  const agendaIdx = agenda ? '_agenda' : '';
  return `${getAlgoliaIndexName(lang)}${agendaIdx}${suffix}`;
};

const initializeFacets = compose(
  reduce((acc, facet) => ({ ...acc, values: { ...acc.values, [facet]: 0 } }), {
    nbResults: 0,
    values: {},
  }),
  values
);

const INITIAL_FILTERS = {
  [FILTER.CLASSIFICATION]: initializeFacets(
    omit(
      [CLASSIFICATION.INNER_PAGE, CLASSIFICATION.PUBLICATION],
      CLASSIFICATION
    )
  ),
  [FILTER.MENU]: initializeFacets(MENU),
  [FILTER.MEDIA_TYPE]: initializeFacets([
    MEDIA_TYPE.RELEASE,
    MEDIA_TYPE.COMMUNICATION,
    MEDIA_TYPE.PHOTO,
    MEDIA_TYPE.INFOGRAPHIC,
    MEDIA_TYPE.VIDEO,
    MEDIA_TYPE.AUDIO,
  ]),
  [FILTER.REGION]: initializeFacets(REGION),
  [FILTER.COMMON_THEMATIC]: initializeFacets(COMMON_THEMATIC),
  [FILTER.PUBLICATION]: initializeFacets(PUBLICATION),
  [FILTER.FINANCIAL_PUBLICATION]: initializeFacets(FINANCIAL_PUBLICATION),
};

// Extract facets prop and remove 'filters." from keys from algolia results
const extractFilters = compose(
  fromPairs,
  map(([key, value]) => [
    replace(/^filters./, '', key),
    { nbResults: sum(values(value)), values: value },
  ]),
  toPairs,
  prop('facets')
);

// Merge all facets
const normalizeFacets = reduce(
  (acc, res) => mergeDeepRight(acc, extractFilters(res)),
  INITIAL_FILTERS
);

export const search = async ({
  query,
  lang,
  sorted,
  facetFilters,
  agenda = false,
  hitsPerPage = 0,
  page = 1,
  ...options
}) => {
  const indexName = getLocalizedIndexedClient(lang, sorted, agenda);

  options.hitsPerPage = hitsPerPage > 0 ? hitsPerPage : undefined;
  // Algolia start page index to 0. We choose an unified pagination system.
  // We start page index to 1
  options.page = page - 1;

  const { results } = await getClient().search([
    {
      indexName,
      query,
      params: compact({
        ...options,
        facetFilters: facetFilters
          ? generateFacetFilters(facetFilters)
          : undefined,
      }),
    },

    ...map(
      (filter) => ({
        indexName,
        query,
        params: compact({
          ...options,
          facets: `filters.${filter}`,
          facetFilters: facetFilters
            ? generateFacetFilters(omit([filter], facetFilters))
            : undefined,
        }),
      }),
      keys(compact(facetFilters || {}))
    ),
  ]);

  return {
    query: results?.[0]?.query,
    count: results?.[0]?.nbHits,
    page: results?.[0]?.page + 1,
    total_page: results?.[0]?.nbPages,
    results: results?.[0]?.hits,
    facets: normalizeFacets(results),
  };
};
